import { useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Cluster,
  EmptyState,
  EyeIcon,
  FlaskIcon,
  GlobeIcon,
  Grid,
  Modal,
  Page,
  QueryState,
  Stack,
  StatStrip,
  cx,
} from '@integra/ui';
import { useAssetPreview, useWorkspacePublications } from '../api/hooks.js';
import type { WorkspacePublication } from '../api/types.js';
import { PlatformPreview, platformLabel, type PostContent } from './preview/PlatformPreview.js';

/**
 * Preview (§20.3) — the workspace publish feed rendered as the target platform's
 * OWN post card, so an operator sees a post exactly as it lands on LinkedIn,
 * Instagram, X, and the rest before it goes out. Sandbox posts are marked
 * preview-only so a demo is never mistaken for a live one (§7.4). Every card
 * opens to a full-size, platform-faithful view.
 */

const SANDBOX_KEY = 'sandbox-channel';

function readString(data: Record<string, unknown>, key: string): string | null {
  const v = data[key];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

function captionOf(data: Record<string, unknown>): { title: string | null; body: string | null } {
  const title = readString(data, 'title') ?? readString(data, 'name');
  const body =
    readString(data, 'text') ??
    readString(data, 'caption') ??
    readString(data, 'description') ??
    readString(data, 'body');
  return { title, body };
}

function fmt(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

/** Build the platform-agnostic post content from a publication + resolved media. */
function contentFor(pub: WorkspacePublication, mediaUrl: string | null): PostContent {
  const { title, body } = captionOf(pub.asset.data);
  return {
    platform: pub.channel.connectorKey,
    authorName: pub.channel.label,
    title,
    body,
    mediaUrl,
    timestamp: pub.lastPublishedAt === null ? null : fmt(pub.lastPublishedAt),
  };
}

/** Resolve the post's media the same way for both the card and the detail view. */
function useMedia(workspaceId: string, pub: WorkspacePublication): string | null {
  const data = pub.asset.data;
  const hasStoredMedia = readString(data, 'mediaPath') !== null;
  const preview = useAssetPreview(workspaceId, hasStoredMedia ? pub.asset.id : '');
  return preview.url ?? readString(data, 'imageUrl');
}

/** One clickable grid tile: the platform label, the live card, and a status line. */
function PreviewCard({
  workspaceId,
  pub,
  onOpen,
}: {
  workspaceId: string;
  pub: WorkspacePublication;
  onOpen: () => void;
}) {
  const mediaUrl = useMedia(workspaceId, pub);
  const isSandbox = pub.channel.connectorKey === SANDBOX_KEY;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex flex-col gap-2 rounded-xl border border-edge bg-surface-muted p-3 text-left transition-shadow duration-fast hover:shadow-card"
    >
      <Cluster gap="sm" align="center">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
          {platformLabel(pub.channel.connectorKey)}
        </span>
        <span className="ml-auto">
          {isSandbox ? (
            <Badge tone="warning">Preview only</Badge>
          ) : (
            <Badge tone={pub.state === 'live' ? 'success' : 'neutral'} dot>
              {pub.state}
            </Badge>
          )}
        </span>
      </Cluster>

      <div className="pointer-events-none">
        <PlatformPreview content={contentFor(pub, mediaUrl)} variant="card" />
      </div>

      <p className="text-[11px] text-ink-muted">
        {isSandbox ? 'Would publish' : 'Published'}
        {pub.lastPublishedAt !== null ? ` · ${fmt(pub.lastPublishedAt)}` : ''}
        <span className="ml-1 text-gold-text opacity-0 transition-opacity group-hover:opacity-100">
          · view full preview
        </span>
      </p>
    </button>
  );
}

/**
 * A device bezel around a preview (§20.3). A phone frame is the honest default —
 * most social posts are seen on a handset — with a desktop frame for the feeds
 * that are read wide. Bezel geometry is arbitrary Tailwind values, no inline
 * style; colour is token utilities.
 */
function DeviceFrame({ device, children }: { device: 'phone' | 'desktop'; children: ReactNode }) {
  if (device === 'desktop') {
    return (
      <div className="mx-auto w-full rounded-lg border border-edge bg-surface p-4 shadow-card">
        {children}
      </div>
    );
  }
  return (
    <div className="mx-auto w-full max-w-[20rem]">
      <div className="rounded-[2rem] border-[10px] border-navy-900 bg-navy-900 p-1.5 shadow-raised">
        <div className="relative overflow-hidden rounded-[1.4rem] bg-surface">
          <div
            className="absolute left-1/2 top-0 z-10 h-4 w-24 -translate-x-1/2 rounded-b-xl bg-navy-900"
            aria-hidden="true"
          />
          <div className="p-3 pt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

/** The click-through: a full-size, platform-faithful render plus post details. */
function PreviewDetailModal({
  workspaceId,
  pub,
  onClose,
}: {
  workspaceId: string;
  pub: WorkspacePublication;
  onClose: () => void;
}) {
  const mediaUrl = useMedia(workspaceId, pub);
  const isSandbox = pub.channel.connectorKey === SANDBOX_KEY;
  const [device, setDevice] = useState<'phone' | 'desktop'>('phone');

  return (
    <Modal
      open
      title={`As it appears on ${platformLabel(pub.channel.connectorKey)}`}
      onClose={onClose}
    >
      <Stack gap="md">
        <Cluster gap="sm" align="center">
          <span className="text-sm font-semibold text-navy-700">{pub.channel.label}</span>
          {isSandbox ? (
            <Badge tone="warning">Sandbox · nothing goes out</Badge>
          ) : (
            <Badge tone={pub.state === 'live' ? 'success' : 'neutral'} dot>
              {pub.state}
            </Badge>
          )}
          <span className="ml-auto">
            <Cluster gap="sm">
              <ScopeTab
                active={device === 'phone'}
                onClick={() => setDevice('phone')}
                label="Phone"
              />
              <ScopeTab
                active={device === 'desktop'}
                onClick={() => setDevice('desktop')}
                label="Desktop"
              />
            </Cluster>
          </span>
        </Cluster>

        <div className="rounded-xl bg-surface-sunken p-4">
          <DeviceFrame device={device}>
            <PlatformPreview content={contentFor(pub, mediaUrl)} variant="full" />
          </DeviceFrame>
        </div>

        <p className="text-xs text-ink-muted">
          {isSandbox ? 'This is a preview — nothing was published.' : 'Published'}
          {pub.lastPublishedAt !== null ? ` · ${fmt(pub.lastPublishedAt)}` : ''}
          {pub.externalUrl !== null ? (
            <>
              {' · '}
              <a
                href={pub.externalUrl}
                target="_blank"
                rel="noreferrer"
                className="text-gold-text underline"
              >
                Open on {platformLabel(pub.channel.connectorKey)} ↗
              </a>
            </>
          ) : null}
        </p>
      </Stack>
    </Modal>
  );
}

export function PreviewPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const publications = useWorkspacePublications(workspaceId);
  const [scope, setScope] = useState<'all' | 'sandbox'>('all');
  const [detail, setDetail] = useState<WorkspacePublication | null>(null);

  const all = publications.data?.data ?? [];
  const sandboxCount = useMemo(
    () => all.filter((p) => p.channel.connectorKey === SANDBOX_KEY).length,
    [all],
  );
  const shown =
    scope === 'sandbox' ? all.filter((p) => p.channel.connectorKey === SANDBOX_KEY) : all;

  return (
    <Page
      title="Preview"
      icon={EyeIcon}
      description="Every post rendered exactly as its platform shows it. Sandbox posts are safe previews — nothing goes out."
      stats={
        all.length > 0 ? (
          <StatStrip
            stats={[
              { icon: EyeIcon, label: 'Publications', value: all.length },
              { icon: FlaskIcon, label: 'Sandbox', value: sandboxCount, tone: 'accent' },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Cluster gap="sm">
          <ScopeTab
            active={scope === 'all'}
            onClick={() => setScope('all')}
            label={`All (${String(all.length)})`}
          />
          <ScopeTab
            active={scope === 'sandbox'}
            onClick={() => setScope('sandbox')}
            label={`Sandbox (${String(sandboxCount)})`}
          />
        </Cluster>

        <QueryState
          loading={publications.isLoading}
          error={publications.error}
          what="the publish feed"
        >
          {shown.length === 0 ? (
            <EmptyState
              icon={EyeIcon}
              title={scope === 'sandbox' ? 'Nothing in the sandbox yet' : 'Nothing published yet'}
              description={
                scope === 'sandbox'
                  ? 'Publish an asset to a sandbox channel to see exactly how it will look — nothing goes out.'
                  : 'Once you publish an asset to a channel, its live preview appears here.'
              }
              features={[
                { icon: GlobeIcon, label: 'Each post as its own platform renders it' },
                { icon: FlaskIcon, label: 'Sandbox previews never publish' },
                { icon: EyeIcon, label: 'Open any card to a full-size view' },
              ]}
            />
          ) : (
            <Grid min="sm">
              {shown.map((pub) => (
                <PreviewCard
                  key={pub.id}
                  workspaceId={workspaceId}
                  pub={pub}
                  onOpen={() => setDetail(pub)}
                />
              ))}
            </Grid>
          )}
        </QueryState>
      </Stack>

      {detail !== null && (
        <PreviewDetailModal
          workspaceId={workspaceId}
          pub={detail}
          onClose={() => setDetail(null)}
        />
      )}
    </Page>
  );
}

function ScopeTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'rounded-md border px-3 py-1.5 text-sm font-medium transition-colors duration-fast',
        active
          ? 'border-navy-700 bg-navy-700 text-ink-inverse'
          : 'border-edge text-navy-700 hover:border-navy-400',
      )}
    >
      {label}
    </button>
  );
}
